import React, { useState } from "react";
import {
  Globe2,
  MapPin,
  Boxes,
  Cpu,
  Building2,
  GraduationCap,
  Database,
  Users,
  PlayCircle,
  TrainFront,
  MessageSquare,
} from "lucide-react";

const regions = [
  {
    key: "international",
    label: "International",
    sub: "USA",
    icon: Globe2,
    clients: [
      {
        name: "ContextLabs",
        tag: "Blockchain & Data Solutions",
        icon: Boxes,
        description:
          "Built a blockchain-based supply chain management solution. Currently implementing data pipelines with custom transformers and sinks.",
      },
      {
        name: "American Kennel Club (AKC)",
        tag: "IoT & Smart Devices",
        icon: Cpu,
        description:
          "Developed backend services and the communication layer for AKC's smart collar — an IoT device for real-time pet monitoring.",
      },
      {
        name: "US1 Industries",
        tag: "Enterprise Modernization",
        icon: Building2,
        description:
          "Migrating a truck management system from Apache Struts to Spring Framework, decomposing the monolith into microservices.",
      },
      {
        name: "Leverage Campus",
        tag: "EdTech Platform",
        icon: GraduationCap,
        description:
          "Complete higher education management software spanning ERP, LMS, SIS, Accounting, and HR modules. MVP delivered.",
      },
      {
        name: "Walchem",
        tag: "IoT & Big Data",
        icon: Database,
        description:
          "Built a client management app and a real-time streaming data engine processing large volumes from IoT monitoring devices.",
      },
      {
        name: "ContactNinja",
        tag: "SaaS · Contact Management",
        icon: Users,
        description:
          "A contact aggregation platform merging contacts from Outlook, Gmail, Facebook, and LinkedIn into one unified app.",
      },
      {
        name: "DCT",
        tag: "OTT Platform",
        icon: PlayCircle,
        description:
          "Working on an OTT CMS platform with Elasticsearch integration, subscription management, and analytics capabilities.",
      },
    ],
  },
  {
    key: "domestic",
    label: "Domestic",
    sub: "India",
    icon: MapPin,
    clients: [
      {
        name: "Indian Railway — Asansol Division",
        tag: "Railway Tech",
        icon: TrainFront,
        description:
          "Running Room Management System and biometric identity management, integrated with existing Railway software.",
      },
      {
        name: "Indian Railway — Jamalpur Division",
        tag: "Railway Tech",
        icon: TrainFront,
        description:
          "Computer-based testing (CBT) system for locomotive pilots, built as part of the existing Railway software ecosystem.",
      },
      {
        name: "LeafyBus",
        tag: "Conversational AI",
        icon: MessageSquare,
        description:
          "An AI WhatsApp chatbot and voice agent integrated with business APIs for automated customer support, bookings, and inquiries.",
      },
      {
        name: "HoiChoi",
        tag: "OTT Platform",
        icon: PlayCircle,
        description:
          "Collaboration with the leading Bengali OTT platform on content management, streaming infrastructure, and platform services.",
      },
    ],
  },
];

export default function ClientSuccessStories() {
  const [activeKey, setActiveKey] = useState("international");
  const active = regions.find((r) => r.key === activeKey);
  const activeIndex = regions.findIndex((r) => r.key === activeKey);
  const highlightWidth = `calc(${100 / regions.length}% - 0.25rem)`;

  return (
    <section className="bg-[#F8F8FB] py-20 px-6 sm:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center mb-4">
          <span className="text-sm md:text-base font-semibold tracking-[0.32em] uppercase font-mono text-[#355dff]">
            Client Success Stories
          </span>
        </div>

        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#111827] leading-tight">
            Client Success Stories
          </h2>

          <div className="w-24 h-1 bg-blue-600 mx-auto mt-5 rounded-full"></div>

          <p className="max-w-4xl mx-auto mt-8 text-lg text-gray-600 leading-relaxed">
            From enterprises in the US to the backbone systems of Indian Railways, here is who we have delivered for — split by market.
          </p>
        </div>

        <div className="flex flex-wrap justify-center items-center gap-3 mt-10 mb-12">
          <div className="relative inline-flex bg-white border border-gray-200 rounded-full p-1 shadow-sm">
            <div
              className="absolute top-1 bottom-1 rounded-full bg-[#355dff] shadow-lg shadow-[#355dff]/30 transition-transform duration-300 ease-out"
              style={{ width: highlightWidth, transform: `translateX(${activeIndex * 100}%)` }}
            />
            {regions.map((region) => {
              const Icon = region.icon;
              const isActive = region.key === activeKey;
              return (
                <button
                  key={region.key}
                  onClick={() => setActiveKey(region.key)}
                  className={`relative z-10 flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${isActive
                    ? "text-white"
                    : "text-gray-600 hover:border-[#355dff]/40 hover:text-[#355dff]"
                    }`}
                >
                  <Icon size={18} />
                  {region.label}
                  <span className="hidden sm:inline text-xs font-normal opacity-70">
                    ({region.sub})
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {active.clients.map((client) => {
            const Icon = client.icon;
            return (
              <div
                key={client.name}
                className="group bg-white rounded-2xl border border-gray-200 p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="w-12 h-12 rounded-xl bg-[#EBF2FF] flex items-center justify-center text-[#355dff] mb-6">
                  <Icon size={20} />
                </div>
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#355dff] mb-3">
                  {client.tag}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
                  {client.name}
                </h3>
                <p className="text-gray-500 text-[15px] leading-7">
                  {client.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}